import {
  list,
  update,
  create,
  destroy,
  IDocumentOptions,
  directUpload,
  importMeasurement,
  combineDocuments,
} from "common/api/document.service";
import {
  DocumentDisplayType,
  DocumentRecord,
  IDocumentData,
  documentFromJSON,
  isMeasurementFile,
} from "common/records/Document";
import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { DocumentActions } from "common/reducers/document.actions";
import { SignedDocumentAsyncActions } from "common/reducers/signedDocument.async.actions";
import { currentJob, currentJobId } from "common/selectors/job.selectors";
import { contractTitle, currentOrgId } from "common/selectors/org.selectors";
import { RootDispatchType, RootGetState } from "common/store";
import { List } from "immutable";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { document } from "common/selectors/document.selectors";
import { ids } from "common/selectors/idsManagement.selectors";
import { DANGER, SUCCESS, addFlashAlert } from "common/components/FlashAlert";
import { documentParamsFromJSON, DocumentParamsRecord } from "common/records/index/DocumentParams";
import { ITaskData } from "common/records/Task";
import { TaskContextOverrides } from "common/records/TaskContext";
import { TaskActions } from "common/reducers/task.actions";
import { Nullable } from "common/types";
import { FolderAsyncActions } from "common/reducers/folder.async.actions";
import { folderParamsFromJSON } from "common/records/index/FolderParams";
import { watchFolderParentId } from "src/router/loaders/helper";
import { currentSignedDocumentId } from "common/selectors/signedDocument.selectors";
import { RsfFormActions } from "common/reducers/rsfForm.actions";
import { EstimateLineItemActions } from "common/reducers/estimateLineItem.actions";
import { MeasurementAsyncActions } from "common/reducers/measurement.async.actions";
import { fetcher, IGlobalRequestOptions } from "common/api/Fetcher";
import { DateFormat } from "common/helpers/Format";
import { exportFileToS3 } from "common/helpers/File";

export const DocumentAsyncActions = {
  listDocuments: (queryParams: DocumentParamsRecord, modelName = "documents"): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      queryParams = setParameters(queryParams, getState());

      dispatch(IdsManagementActions.fetchModel(modelName, queryParams));

      try {
        const response = await list(queryParams);
        dispatch(DocumentActions.receiveDocuments(response.documents));

        const ids = List(response.documents.map((document: IDocumentData) => document.id));
        dispatch(IdsManagementActions.receiveModel(modelName, ids, response?.meta?.pagination));
      } catch (response) {
        handleErrors(response);
        dispatch(IdsManagementActions.receiveModel(modelName, List()));
      }
    };
  },
  updateDocument: (
    documentId: number,
    isSingleUpdate = true,
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const record = document(getState(), { documentId });
      if (!record) {
        addFlashAlert("danger", "Document not found");
        return;
      }

      if (isSingleUpdate) dispatch(IdsManagementActions.fetchModel("documentsUpdate"));
      try {
        const response = await (options ? update(record, options) : update(record));
        if (isSingleUpdate) addFlashAlert("success", "Saved document successfully");

        dispatch(DocumentActions.receiveDocument(response.document));
      } catch (error) {
        handleErrors(error);
      } finally {
        if (isSingleUpdate) dispatch(IdsManagementActions.setModelLoaded("documentsUpdate"));
      }
    };
  },
  destroyDocument: (documentId: number, notify = true, options?: IGlobalRequestOptions): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const record = document(getState(), { documentId });
      if (!record) {
        addFlashAlert("danger", "Document not found");
        return;
      }

      dispatch(IdsManagementActions.fetchModel("documentsUpdate"));
      try {
        await (options ? destroy(record, options) : destroy(record));

        dispatch(DocumentActions.removeDocument(documentId));
        dispatch(IdsManagementActions.removeId(documentId, "documents"));

        if (notify) {
          addFlashAlert("success", "Document deleted successfully");
        }
      } catch (error) {
        handleErrors(error);
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("documentsUpdate"));
      }
    };
  },
  batchUpdateDocuments: (documentIds: List<number>): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(IdsManagementActions.fetchModel("documentsUpdate"));
      await Promise.all(
        documentIds.map((documentId) => dispatch(DocumentAsyncActions.updateDocument(documentId, false))),
      );
      dispatch(IdsManagementActions.setModelLoaded("documentsUpdate"));
      addFlashAlert("success", "Documents updated");
    };
  },
  moveDocuments: (folderId: number, modelName = "documents"): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const documentIds = ids(getState(), { modelName }) as List<number>;

      dispatch(IdsManagementActions.fetchModel(modelName));
      dispatch(DocumentActions.editDocuments(documentIds, "folder_id", folderId));

      await dispatch(DocumentAsyncActions.batchUpdateDocuments(documentIds));

      documentIds.forEach((documentId) => {
        dispatch(IdsManagementActions.removeId(documentId, "documents"));
      });
      dispatch(IdsManagementActions.receiveModel(modelName, List()));
    };
  },
  createCustomFormDocument: (customFormId: string, jobId: number): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType) => {
      const pdfUrl = fetcher.joinUrls(
        "api/v1/jobs",
        jobId.toString(),
        "custom_forms",
        `${customFormId}.pdf?save_tmp=true`,
      );
      const fileName = "Finance Application - " + DateFormat(new Date(), "short-hyphenated") + ".pdf";

      try {
        const response = await fetcher.fetch(pdfUrl);
        const pdfBlob = await response.blob();

        const tempDocumentIdHeader = response.headers.get("document-id");
        const tempDocumentId = tempDocumentIdHeader ? parseInt(tempDocumentIdHeader) : NaN;

        if (Number.isNaN(tempDocumentId)) {
          throw new Error("Invalid document ID received from server.");
        }

        dispatch(
          DocumentActions.receiveDocument({
            name: fileName,
            id: tempDocumentId,
            display: "do_not_display",
            documentable_id: jobId,
            documentable_type: "Job",
            file: { url: URL.createObjectURL(pdfBlob) },
          }),
        );

        dispatch(
          SignedDocumentAsyncActions.initDraft({
            document_id: tempDocumentId,
            status: "created",
            metadata: { custom_form_id: customFormId },
          }),
        );
        return true;
      } catch (response) {
        handleErrors(response, true);
        return false;
      }
    };
  },
  createContractDocument: (estimateId: number, jobId: number, documentName?: string): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType) => {
      const pdfUrl = fetcher.joinUrls(
        "api/v1/jobs",
        jobId.toString(),
        "estimates",
        `${estimateId}.pdf?doc_type=contract&save_tmp=true`,
      );
      const fileName = documentName || "Contract - " + DateFormat(new Date(), "short-hyphenated") + ".pdf";

      try {
        const response = await fetcher.fetch(pdfUrl);
        const pdfBlob = await response.blob();

        const tempDocumentIdHeader = response.headers.get("document-id");
        const tempDocumentId = tempDocumentIdHeader ? parseInt(tempDocumentIdHeader) : NaN;

        if (Number.isNaN(tempDocumentId)) {
          throw new Error("Invalid document ID received from server.");
        }

        dispatch(
          DocumentActions.receiveDocument({
            name: fileName,
            id: tempDocumentId,
            display: "do_not_display",
            documentable_id: jobId,
            documentable_type: "Job",
            file: { url: URL.createObjectURL(pdfBlob) },
          }),
        );

        dispatch(
          SignedDocumentAsyncActions.initDraft({
            document_id: tempDocumentId,
            estimate_id: estimateId,
            status: "created",
          }),
        );
        return true;
      } catch (response) {
        handleErrors(response, true);
        return false;
      }
    };
  },
  createDocumentFromBlob: (blob: Blob, folderId: Nullable<number>): ThunkResult<Promise<Nullable<number>>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const job = currentJob(getState());
      if (!job) {
        addFlashAlert("danger", "Unable to locate current job");
        return;
      }

      let presigned: { id: string; url: string } | null = null;
      try {
        const { urls } = await directUpload(1, "Job", job.get("id"));
        if (urls.length !== 1) {
          addFlashAlert("warning", "Unable to initiate document upload. Please try again.");
          return;
        }

        presigned = urls[0];
      } catch (e) {
        handleErrors(e, true);
        return;
      }

      const title = contractTitle(getState(), {});
      const prefix = job?.get("name") || title;
      const timestamp = DateFormat(new Date(), "short-hyphenated").replace(":", "");
      const suffix = job?.get("name") ? ` - ${title}` : "";
      const file = new File([blob], `${prefix} - ${timestamp}${suffix}.pdf`, { type: "application/pdf" });

      try {
        await exportFileToS3(presigned.url, file);
      } catch (e) {
        addFlashAlert("danger", "Unable to upload document. Please try again.");
        handleErrors(e, false);
        return;
      }

      const record = documentFromJSON({
        content_type: "application/pdf",
        display: "do_not_display",
        documentable_id: job.get("id"),
        documentable_type: "Job",
        file_queue: file,
        folder_id: folderId,
        name: file.name,
        uuid: presigned.id,
      });

      try {
        const response = await create(record);
        dispatch(DocumentActions.receiveDocument({ ...response.document, file_queue: file }));

        return response.document.id;
      } catch (e) {
        addFlashAlert("danger", "Unable to save document. Please try again.");
        handleErrors(e, false);
        return;
      }
    };
  },
  batchCreateDocument: (
    files: any[],
    options: IDocumentOptions,
    notify = true,
    taskContextOverrides: TaskContextOverrides = {},
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, _getState: RootGetState) => {
      dispatch(IdsManagementActions.fetchModel("documents"));
      try {
        const {
          documentableType,
          documentableId,
          display,
          folder_id,
          sort_order,
          autoImportMeasurements = false,
          replacedNegativeIds,
        } = options;
        const fileCount = files.length;
        const { urls } = await directUpload(fileCount, documentableType, documentableId);

        if (autoImportMeasurements) {
          dispatch(IdsManagementActions.fetchModel("documentImporting"));
        }

        let docSuccess = 0;
        // Iterate `files` (not `urls`) so `index` indexes the file/staging-id pair
        // — `replacedNegativeIds[index]` must align with `files[index]`. If the
        // server ever returns fewer URLs than files, the surplus files skip cleanly
        // and their staging records remain for the next save.
        await Promise.all(
          files.map(async (file, index) => {
            const obj = urls[index];
            if (!obj) return;
            try {
              await fetch(obj.url, { body: file, method: "PUT", headers: { "Content-Type": file.type } });

              const record = documentFromJSON({
                uuid: obj.id,
                name: file.name,
                display: display || "do_not_display",
                documentable_id: documentableId,
                documentable_type: documentableType,
                folder_id: folder_id,
                sort_order: sort_order,
              });
              const response = await create(record);

              // Preserve the server's content_type when File.type is empty (drag-and-drop quirk
              // where the browser doesn't populate the MIME). Empty file.type would blank a valid
              // server value and break downstream content-type checks (e.g. PDF detection).
              dispatch(
                DocumentActions.receiveDocument({
                  ...response.document,
                  content_type: file.type || response.document.content_type,
                }),
              );
              dispatch(
                DocumentAsyncActions.processDocument(
                  response.document,
                  { autoImport: autoImportMeasurements },
                  taskContextOverrides,
                ),
              );

              // Phase 3 cleanup: drop the staging record now that the server minted the real one.
              const replacedId = replacedNegativeIds?.[index];
              if (replacedId !== undefined && replacedId < 0) {
                if (documentableType === "EstimateLineItem") {
                  dispatch(EstimateLineItemActions.removeDocumentId(documentableId, replacedId));
                }
                dispatch(DocumentActions.removeDocument(replacedId));
              }

              docSuccess += 1;
            } catch (response) {
              dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
              handleErrors(response, notify);
            }
          }),
        );

        if (notify && docSuccess === files.length) {
          addFlashAlert("success", docSuccess + "/" + files.length + " Documents successfully uploaded");
        }
      } catch (response) {
        handleErrors(response, notify);
        dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
      } finally {
        dispatch(IdsManagementActions.setModelLoaded("documents"));
      }
    };
  },
  processDocument: (
    document: IDocumentData,
    options: { autoImport?: boolean } = {},
    overrides: TaskContextOverrides = {},
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      if (document.documentable_type === "EstimateLineItem") {
        dispatch(EstimateLineItemActions.pushDocumentId(document.documentable_id, document.id));
      } else {
        dispatch(IdsManagementActions.pushId(document.id, "documents"));
      }

      const { task } = document;
      if (task) {
        const context = {
          model: "document",
          action: overrides.action ?? "processDocumentTaskHandler",
          action_payload: {
            id: document.id,
            autoImport: options.autoImport || false,
            // Threaded so error handlers can clean up the parent's id reference when the
            // task fails — without these, removing from documents.byId leaves a dangling
            // id on estimateLineItems.byId[N].document_ids.
            documentable_type: document.documentable_type,
            documentable_id: document.documentable_id,
            ...(overrides.action_payload ?? {}),
          },
          error_action: overrides.error_action ?? "errorTaskHandler",
        };
        task.context = context;
        dispatch(TaskActions.receiveTask(task));
      }
    };
  },
  silentTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(DocumentActions.editDocument(payload.id, "import_type", task.results.import_type));
    };
  },
  silentErrorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      removeDocumentFromOwner(dispatch, payload);
    };
  },
  processDocumentTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(DocumentActions.editDocument(payload.id, "import_type", task.results.import_type));
      const name = task.results.class_name || "Task Completion";
      const shouldAutoImport = payload.autoImport || false;

      if (shouldAutoImport) {
        const doc = document(getState(), { documentId: payload.id });
        if (!doc) {
          dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
          return;
        }

        // [RSF-25472] Tasks are returned multiple times. Testing here to return early on the first task completion.
        if (task.results.import_type === "no_importer") {
          addFlashAlert(DANGER, "No importer found for this document type. Please try another file.");
          dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
          return;
        }

        if (isMeasurementFile(doc)) {
          dispatch(DocumentAsyncActions.importMeasurementDocument(doc));
          addFlashAlert(SUCCESS, `${name} Successful.`);
        }
      } else {
        dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
        addFlashAlert(SUCCESS, `${name} Successful.`);
      }
    };
  },
  errorTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      const errorMessage =
        task?.results?.error_message ||
        "There were problems processing your task. Please try again. If the problem persists contact support.";

      addFlashAlert(DANGER, errorMessage);

      removeDocumentFromOwner(dispatch, payload);
    };
  },
  importMeasurementDocument: (document: DocumentRecord, options?: IGlobalRequestOptions): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        dispatch(DocumentActions.fetchDocument(document.id));

        const response = await (options ? importMeasurement(document, options) : importMeasurement(document));

        dispatch(DocumentActions.receiveDocument(response.document));
        const { task } = response.document;
        if (task) {
          const context = {
            model: "document",
            action: "importMeasurementDocumentTaskHandler",
            action_payload: { id: document.id },
            error_action: "errorImportMeasurementDocumentTaskHandler",
          };
          task.context = context;
          dispatch(DocumentActions.fetchDocument(document.id));
          dispatch(TaskActions.receiveTask(task));
        }
      } catch (response) {
        handleErrors(response);
        dispatch(DocumentActions.setDocumentLoaded(document.id));
        dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
      }
    };
  },

  importMeasurementDocumentTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(DocumentActions.setDocumentLoaded(payload.id));
      // Auto-import is always expected for measurement document imports
      dispatch(MeasurementAsyncActions.loadMeasurement());
      dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
      addFlashAlert(SUCCESS, "Measurement import completed.");
    };
  },

  errorImportMeasurementDocumentTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      dispatch(DocumentActions.setDocumentLoaded(payload.id));
      // Error occurred during measurement import - always show error for auto-imports
      dispatch(IdsManagementActions.setModelLoaded("documentImporting"));
      addFlashAlert(DANGER, "Measurement import failed. Please try again. If the problem persists contact support.");
    };
  },

  loadOrgResourceOrJobDocuments: (
    jobOrOrgId: number,
    page: number,
    perPage: number,
    folderId: Nullable<number>,
    documentableType: "Org" | "Job",
    content_type?: string,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const documentableId = jobOrOrgId;
      if (!folderId) {
        folderId = await watchFolderParentId("Documents", documentableType);
        if (!folderId) {
          return;
        }
      }

      dispatch(
        FolderAsyncActions.listFolders(folderParamsFromJSON({ id: folderId, folderable_type: documentableType })),
      );

      const documentParamsData = {
        documentableType,
        documentableId,
        page,
        per_page: perPage,
        folder_id: folderId,
        ...(documentableType === "Org" && { display: "resource" as DocumentDisplayType }),
        content_type: content_type,
      };
      dispatch(DocumentAsyncActions.listDocuments(documentParamsFromJSON(documentParamsData)));
    };
  },
  combine: (
    document_ids: List<number>,
    name: string,
    jobId: number,
    options?: IGlobalRequestOptions,
  ): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      try {
        const { document } = await (options
          ? combineDocuments(document_ids, name, jobId, options)
          : combineDocuments(document_ids, name, jobId));
        const task = document.task;
        if (task) {
          const context = {
            model: "document",
            action: "combineTaskHandler",
            action_payload: { id: document.id },
            error_action: "errorTaskHandler",
          };
          task.context = context;
          dispatch(TaskActions.receiveTask(task));
        }
      } catch (response) {
        handleErrors(response);
      }
    };
  },
  combineTaskHandler: (task: ITaskData, payload: any = {}): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState) => {
      const { document } = task.results;
      if (document) {
        dispatch(DocumentActions.receiveDocument(document));
        dispatch(TaskActions.receiveTask(task));

        const id = currentSignedDocumentId(getState());
        dispatch(
          RsfFormActions.update({
            rootPath: ["signedDocuments", "byId", id],
            name: "document_id",
            value: document.id,
          }),
        );
      }
    };
  },
};

/**
 * Cleanup helper used by both silent and non-silent task error handlers. Removes the
 * document's id from its owning entity (currently EstimateLineItem) before deleting
 * the documents.byId record, so the parent doesn't carry a dangling reference. The
 * documentable info comes from the task's action_payload which `processDocument`
 * threads through.
 */
const removeDocumentFromOwner = (
  dispatch: RootDispatchType,
  payload: { id: number; documentable_type?: string; documentable_id?: number },
): void => {
  if (payload.documentable_type === "EstimateLineItem" && payload.documentable_id) {
    dispatch(EstimateLineItemActions.removeDocumentId(payload.documentable_id, payload.id));
  }
  dispatch(DocumentActions.removeDocument(payload.id));
};

export const SILENT_DOCUMENT_CONTEXT: TaskContextOverrides = {
  action: "silentTaskHandler",
  error_action: "silentErrorTaskHandler",
};

export const setParameters = (queryParams: DocumentParamsRecord, state: RootState) => {
  if (queryParams.documentableId) {
    return queryParams;
  }

  switch (queryParams.documentableType) {
    case "Job":
      const jobId = currentJobId(state);
      if (jobId) {
        queryParams = queryParams.set("documentableId", jobId);
      }
      break;
    case "Org":
      const orgId = currentOrgId(state);
      if (orgId) {
        queryParams = queryParams.set("documentableId", orgId);
      }
      break;
    default:
  }
  return queryParams;
};
